<template>
  <div>
    <div class="payment-method-options">
      <div class="payment-method-options-row">
        <div class="payment-method-option" v-if="order.can_charge_card_on_file" @click.prevent="$e => select_payment_method('card_on_file')" :class="{selected: this.payment_method == 'card_on_file'}">
          <span><strong>{{ order.card.brand }}</strong> ending in <em>{{ order.card.last_4 }}</em></span>
        </div>
        <div class="payment-method-option" @click.prevent="$e => select_payment_method('new_card')" :class="{selected: this.payment_method == 'new_card'}">
          <span>+ Add Credit Card</span>
        </div>
        <div class="payment-method-option wallet-option" v-if='applePay || googlePay' @click.prevent="$e => select_payment_method('wallet')" :class="{selected: this.payment_method == 'wallet'}">
          <button aria-label="Apple Pay" v-if='this.applePay' @click.prevent='$e => null' class='apple-pay-button-plain'></button>
          <div v-if="this.googlePay" class="google-pay-button"></div>
        </div>
      </div>
      <div class="new-card" v-if="this.payment_method == 'new_card'">
        <StripeCard ref="stripe" />
      </div>
    </div>
  </div>
</template>

<script>
  import { loadStripe } from '@stripe/stripe-js'
import StripeCard from 'app/components/orders/StripeCard'
import LineItemsMixin from 'app/mixins/LineItemsMixin'
import TotalsMixin from 'app/mixins/TotalsMixin'

  export default {
    mixins: [TotalsMixin, LineItemsMixin],
    props: ['order', 'prices', 'admin'],
    async mounted() {
      this.stripe = await loadStripe(window.STRIPE_PUBLISHABLE_KEY)
      const elements = this.stripe.elements()

      if (this.order.can_charge_card_on_file) {
        this.payment_method = 'card_on_file'
      } else {
        this.payment_method = 'new_card'
      }

      // setup the initial payment reqeust
      this.paymentRequest = this.stripe.paymentRequest({
        country: 'US',
        currency: 'usd',
        total: {
          label: 'Order total',
          amount: 0,
        },

        displayItems: [],
        disableWallets: ['link']
      });

      // handle the payment response
      // Need this to update the order (shipping, card info) and proceed
      // to the thank you page
      this.paymentRequest.on('token', async (ev) => {
        ev.complete('success');

        // call any / all callbacks
        while(this.on_token_callbacks.length > 0) {
          const callback = this.on_token_callbacks.pop()
          callback(ev.token, this.payment_method)
        }
      });

      this.paymentRequest.on('cancel', (ev) => {
        // call any / all callbacks
        while(this.on_token_callbacks.length > 0) {
          const callback = this.on_token_callbacks.pop()
          callback({ canceled: true })
        }
      });

      // check for wallet availability before showing the request button
      (async () => {
        // Check the availability of the Payment Request API first.
        const result = await this.paymentRequest.canMakePayment();
        if (result) {
          this.applePay = result.applePay
          this.googlePay = result.googlePay
        }
      })();
    },
    data: () => {
      return {
        payment_method: null,
        applePay: false,
        googlePay: false,
        paymentRequest: null,
        on_token_callbacks: []
      }
    },
    computed: {
      orderDetails() {
        const details = {
          total: {
            label: 'Order total',
            amount: Math.round((this.raw_total - this.order.charged_amount) * 100),
          },
        }

        // update the display line items
        const displayItems = []
        this.line_items.forEach((lineItem) => {
          let label = lineItem.name
          if (lineItem.quantity > 1) {
            label = `${label} (${lineItem.quantity})`
          }

          displayItems.push({
            label: label,
            amount: Math.round(lineItem.total * 100)
          })
        })

        // show any discount
        if (this.calculated_promo_code_discount > 0) {
          displayItems.push({
            label: 'Discount',
            amount: Math.round(this.calculated_promo_code_discount * -100)
          })
        }

        // add shipping
        if (this.shipping > 0) {
          displayItems.push({
            label: 'Shipping',
            amount: Math.round(this.shipping * 100)
          })
        }

        // add sales tax
        if (this.order.tax_rate != null) {
          displayItems.push({
            label: this.tax_label,
            amount: Math.round(this.calculated_tax * 100)
          })
        }

        // add any previous payements
        if (this.order.charges && this.order.charges.length > 0) {
          this.order.charges.forEach((charge) => {
            displayItems.push({
              label: `Payment on ${charge.date_created}`,
              amount: Math.round(charge.amount * -100)
            })
          })
        }

        details.displayItems = displayItems
        return details
      },
    },
    methods: {
      select_payment_method(method) {
        this.payment_method = method;
      },
      create_token_if_needed(callback) {
        if (this.payment_method == 'new_card') {
          return this.$refs.stripe.create_token(result => {
            callback(result, this.payment_method)
          });
        } else if (this.payment_method == 'wallet') {
          // add the callback to the list
          this.on_token_callbacks.push(callback)

          // update the paymentRequest with the new total and details
          this.paymentRequest.update(this.orderDetails)
          this.paymentRequest.show()
          return
        } else {
          // the server will take care of charging the card on file
          // or comping the order
          callback({}, this.payment_method)
          return
        }
      },
    },
    components: { StripeCard }
  }
</script>
